import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  People as PeopleIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import EventsLoggerService, { EventsLoggerResponse, EventObject } from '../../services/events-logger.service';

interface BundlePurchaseEventData {
  bundleType: string;
  name: string;
  email: string;
  planId: string;
  planUnits: number;
  amount: number;
  couponId: string;
  flow?: string;
  step: string;
  timestamp: string;
  paymentStatus?: string;
  isNewCustomer?: boolean;
  response?: any;
}

interface BundlePurchaseEvent {
  _id: string;
  email: string;
  event_type: string;
  event_data: BundlePurchaseEventData;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface AnalyticsData {
  totalCount: number;
  totalAmount: number;
  bundleTypeCounts: { [key: string]: number };
  bundleTypeAmounts: { [key: string]: number };
  successCount: number;
  startCount: number;
  newUserCount: number;
  existingUserCount: number;
}

interface CaseyWorkshopAnalyticsProps {
  open: boolean;
  onClose: () => void;
}

const CaseyWorkshopAnalytics: React.FC<CaseyWorkshopAnalyticsProps> = ({ open, onClose }) => {
  const [events, setEvents] = useState<BundlePurchaseEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<BundlePurchaseEvent[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalCount: 0,
    totalAmount: 0,
    bundleTypeCounts: {},
    bundleTypeAmounts: {},
    successCount: 0,
    startCount: 0,
    newUserCount: 0,
    existingUserCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to check if email is from easywebinar.com domain
  const isEasyWebinarEmail = (email: string): boolean => {
    return email.toLowerCase().endsWith('@easywebinar.com');
  };

  // Helper function to filter out easywebinar.com emails
  const filterEasyWebinarEmails = (events: BundlePurchaseEvent[]): BundlePurchaseEvent[] => {
    return events.filter(event => !isEasyWebinarEmail(event.event_data.email));
  };
  
  // Filters
  const [bundleTypeFilter, setBundleTypeFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [stepFilter, setStepFilter] = useState<string>('all');
  const [flowFilter, setFlowFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await EventsLoggerService.getEventsByType('BUNDLE_PURCHASE');
      
      if (response.data.success && response.data.events) {
        const allEvents = (response.data.events as any) as BundlePurchaseEvent[];
        // Filter out easywebinar.com emails
        const events = filterEasyWebinarEmails(allEvents);
        setEvents(events);
        // Show success and error events initially
        const relevantEvents = events.filter(event => 
          event.event_data.step === 'success' || event.event_data.step === 'error'
        );
        setFilteredEvents(relevantEvents);
        calculateAnalytics(events);
      } else {
        setError('Failed to fetch events data');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching events');
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (eventsData: BundlePurchaseEvent[]) => {
    // Filter to include both success and error events
    const relevantEvents = eventsData.filter(event => 
      event.event_data.step === 'success' || event.event_data.step === 'error'
    );
    
    const analyticsData: AnalyticsData = {
      totalCount: relevantEvents.length,
      totalAmount: 0,
      bundleTypeCounts: {},
      bundleTypeAmounts: {},
      successCount: 0,
      startCount: 0,
      newUserCount: 0,
      existingUserCount: 0
    };

    relevantEvents.forEach(event => {
      const { bundleType, amount, step, flow, isNewCustomer } = event.event_data;
      
      // Count by bundle type
      analyticsData.bundleTypeCounts[bundleType] = (analyticsData.bundleTypeCounts[bundleType] || 0) + 1;
      
      // Sum amounts by bundle type (only for success events)
      if (amount && step === 'success') {
        analyticsData.bundleTypeAmounts[bundleType] = (analyticsData.bundleTypeAmounts[bundleType] || 0) + amount;
        analyticsData.totalAmount += amount;
      }
      
      // Count by step
      if (step === 'success') analyticsData.successCount++;
      if (step === 'error') analyticsData.startCount++; // Using startCount to track error count
      
      // Count by flow - use flow if available, otherwise derive from isNewCustomer for audit bundles
      if (flow === 'newUser') {
        analyticsData.newUserCount++;
      } else if (flow === 'existingUser') {
        analyticsData.existingUserCount++;
      } else if (bundleType === 'auditBundle' && isNewCustomer !== undefined) {
        // For audit bundles, derive flow from isNewCustomer
        if (isNewCustomer) {
          analyticsData.newUserCount++;
        } else {
          analyticsData.existingUserCount++;
        }
      }
    });

    setAnalytics(analyticsData);
  };

  const applyFilters = () => {
    // Start with success and error events, and filter out easywebinar.com emails
    let filtered = events.filter(event => 
      (event.event_data.step === 'success' || event.event_data.step === 'error') &&
      !isEasyWebinarEmail(event.event_data.email)
    );

    // Filter by bundle type
    if (bundleTypeFilter !== 'all') {
      filtered = filtered.filter(event => event.event_data.bundleType === bundleTypeFilter);
    }

    // Filter by step
    if (stepFilter !== 'all') {
      filtered = filtered.filter(event => event.event_data.step === stepFilter);
    }

    // Filter by flow
    if (flowFilter !== 'all') {
      if (flowFilter === 'empty') {
        filtered = filtered.filter(event => !event.event_data.flow && !(event.event_data.bundleType === 'auditBundle' && event.event_data.isNewCustomer !== undefined));
      } else {
        filtered = filtered.filter(event => {
          const { flow, bundleType, isNewCustomer } = event.event_data;
          
          // Use flow if available
          if (flow) {
            return flow === flowFilter;
          }
          
          // For audit bundles, derive flow from isNewCustomer
          if (bundleType === 'auditBundle' && isNewCustomer !== undefined) {
            const derivedFlow = isNewCustomer ? 'newUser' : 'existingUser';
            return derivedFlow === flowFilter;
          }
          
          return false;
        });
      }
    }

    // Filter by date range
    if (dateFromFilter) {
      const fromDate = new Date(dateFromFilter);
      filtered = filtered.filter(event => new Date(event.createdAt) >= fromDate);
    }

    if (dateToFilter) {
      const toDate = new Date(dateToFilter);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(event => new Date(event.createdAt) <= toDate);
    }

    // Filter by search term (searches both name and email)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(event => 
        event.event_data.name.toLowerCase().includes(searchLower) ||
        event.event_data.email.toLowerCase().includes(searchLower)
      );
    }

    setFilteredEvents(filtered);
    calculateAnalytics(filtered);
  };

  const clearFilters = () => {
    setBundleTypeFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setStepFilter('all');
    setFlowFilter('all');
    setSearchTerm('');
    // Show success and error events, and filter out easywebinar.com emails
    const relevantEvents = events.filter(event => 
      (event.event_data.step === 'success' || event.event_data.step === 'error') &&
      !isEasyWebinarEmail(event.event_data.email)
    );
    setFilteredEvents(relevantEvents);
    calculateAnalytics(events);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount); // Amount is already in dollars
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getBundleTypeColor = (bundleType: string) => {
    switch (bundleType) {
      case 'professionalBundle':
        return 'primary';
      case 'auditBundle':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStepColor = (step: string) => {
    switch (step) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'start':
        return 'info';
      default:
        return 'default';
    }
  };

  const getFlowColor = (flow?: string) => {
    switch (flow) {
      case 'newUser':
        return 'success';
      case 'existingUser':
        return 'warning';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    if (open) {
      fetchEvents();
    }
  }, [open]);

  useEffect(() => {
    applyFilters();
  }, [bundleTypeFilter, dateFromFilter, dateToFilter, stepFilter, flowFilter, searchTerm, events]);

  const uniqueBundleTypes = Array.from(new Set(events.map(event => event.event_data.bundleType)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div">
            Casey's Workshop Analytics
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Analytics Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <PeopleIcon color="primary" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Total Events
                        </Typography>
                        <Typography variant="h6">
                          {analytics.totalCount}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <AttachMoneyIcon color="success" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Total Revenue
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(analytics.totalAmount)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Success / Error
                        </Typography>
                        <Typography variant="h6">
                          {analytics.successCount} / {analytics.startCount}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <CategoryIcon color="secondary" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Bundle Types
                        </Typography>
                        <Typography variant="h6">
                          {Object.keys(analytics.bundleTypeCounts).length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Bundle Type Breakdown */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Bundle Type Breakdown
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(analytics.bundleTypeCounts).map(([bundleType, count]) => (
                  <Grid item xs={12} sm={6} md={4} key={bundleType}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          {bundleType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {count} events
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatCurrency(analytics.bundleTypeAmounts[bundleType] || 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Filters */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <FilterListIcon />
                <Typography variant="h6">Filters</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Refresh Data">
                  <IconButton onClick={fetchEvents} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button onClick={clearFilters} size="small" variant="outlined">
                  Clear Filters
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Bundle Type</InputLabel>
                    <Select
                      value={bundleTypeFilter}
                      label="Bundle Type"
                      onChange={(e) => setBundleTypeFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {uniqueBundleTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Step</InputLabel>
                    <Select
                      value={stepFilter}
                      label="Step"
                      onChange={(e) => setStepFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Steps</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Flow</InputLabel>
                    <Select
                      value={flowFilter}
                      label="Flow"
                      onChange={(e) => setFlowFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Flows</MenuItem>
                      <MenuItem value="newUser">New User</MenuItem>
                      <MenuItem value="existingUser">Existing User</MenuItem>
                      <MenuItem value="empty">No Flow</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date From"
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date To"
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              
              {/* Search Field */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={12} md={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search by Name or Email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Enter name or email to search..."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Events Table */}
            <Typography variant="h6" gutterBottom>
              Bundle Purchase Events ({filteredEvents.length})
            </Typography>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Bundle Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell>Flow</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEvents.map((event) => (
                    <TableRow key={event._id} hover>
                      <TableCell>{event.event_data.name}</TableCell>
                      <TableCell>{event.event_data.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={event.event_data.bundleType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          color={getBundleTypeColor(event.event_data.bundleType) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {event.event_data.amount ? formatCurrency(event.event_data.amount) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={event.event_data.step}
                          color={getStepColor(event.event_data.step) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const { flow, bundleType, isNewCustomer } = event.event_data;
                          
                          // Use flow if available
                          if (flow) {
                            return (
                              <Chip
                                label={flow}
                                color={getFlowColor(flow) as any}
                                size="small"
                              />
                            );
                          }
                          
                          // For audit bundles, derive flow from isNewCustomer
                          if (bundleType === 'auditBundle' && isNewCustomer !== undefined) {
                            const derivedFlow = isNewCustomer ? 'newUser' : 'existingUser';
                            return (
                              <Chip
                                label={derivedFlow}
                                color={getFlowColor(derivedFlow) as any}
                                size="small"
                                title={`Derived from isNewCustomer: ${isNewCustomer}`}
                              />
                            );
                          }
                          
                          // No flow data available
                          return (
                            <Chip
                              label="N/A"
                              color="default"
                              size="small"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>{formatDate(event.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseyWorkshopAnalytics;
